import { CheckboxVisibility, DetailsListLayoutMode, MessageBarType, Selection, SelectionMode } from '@fluentui/react';
import {
  Button,
  buttonStylesDanger,
  buttonStylesPrimary,
  ConfirmDialog,
  DetailsList,
  MessageBar,
  useTheme,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useRef, useState } from 'react';

import { IngressDeleteRequest } from '../../../../../admin-center/gen/fw_management/fw_management_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { useUser } from '../../../../../utils/hooks';
import { messageBarContainerStyles } from '../../../common/common.styles';
import { buildUserContext, getDummyList } from '../../../common/utils';
import SkeletonLoader from '../../SkeletonLoader/SkeletonLoader';
import { actionAreaStyle, firewallManagementTableStyles, TableBackgroundStyle } from '../FWManagement.styles';
import { getMessageBarProps } from '../FWManagement.utils';

interface InboundIpItem {
  inboundIP: string;
}

interface IPTableProps {
  fwIps: string[];
  onSubmitSuccess: () => Promise<void>;
  isLoading: boolean;
  onAddButtonClick: () => void;
}

const dummyList = getDummyList<InboundIpItem>(5);

export default function IPTable({ fwIps, onSubmitSuccess, isLoading, onAddButtonClick }: IPTableProps) {
  const service = useAdminCenterService();
  const theme = useTheme();
  const user = useUser();
  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  const [responseMessageType, setResponseMessageType] = useState<MessageBarType>(MessageBarType.info);
  const ipData: InboundIpItem[] = fwIps.map((ip) => ({ inboundIP: ip }));
  const [showMessageBar, setShowMessagebar] = useState<boolean>(false);
  const [tableSelections, setTableSelections] = useState<InboundIpItem[]>([]);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);
  const [isDeletionPending, setIsDeletionPending] = useState<boolean>(false);

  const selectionRef = useRef<Selection>(
    new Selection({
      onSelectionChanged: () => {
        const selectedItems = selectionRef.current.getSelection();
        handleSelectionChanged(selectedItems);
      },
      getKey: (item: any) => item.userEmail,
    })
  );

  const handleSelectionChanged = useCallback((items: any[]) => {
    setTableSelections(items);
  }, []);

  useEffect(() => {
    setTableSelections([]);
  }, [isLoading]);

  const handleDelete = async () => {
    setIsDeletionPending(true);
    try {
      const userContext = await buildUserContext(user);
      const ipAddresses = tableSelections.map((item) => item.inboundIP);
      const request: IngressDeleteRequest = {
        deletedFwIps: ipAddresses,
        modifiedBy: user?.name ?? '',
        timestamp: Date.now() / 1000,
        userContext: userContext,
      };
      const response = await service.deleteIngressRules(request);

      if (response.statusUpdate) {
        const { type, message } = getMessageBarProps(response.statusUpdate);
        setResponseMessage(message);
        setResponseMessageType(type);
      } else {
        setResponseMessage('Failed to Delete');
        setResponseMessageType(MessageBarType.error);
      }
      await onSubmitSuccess();
    } catch (error) {
      setResponseMessage(`Error: ${error}`);
      setResponseMessageType(MessageBarType.error);
    } finally {
      setShowMessagebar(true);
      setIsDeletionPending(false);
      setShowDeleteConfirmationDialog(false);
      setTableSelections([]);
    }
  };

  return (
    <>
      <div style={TableBackgroundStyle(theme)}>
        {showMessageBar && (
          <div style={messageBarContainerStyles}>
            <MessageBar timeout={6000} onDismiss={() => setShowMessagebar(false)} messageBarType={responseMessageType}>
              {responseMessage}
            </MessageBar>
          </div>
        )}
        <div style={actionAreaStyle}>
          <Button styles={buttonStylesPrimary} text="Add" onClick={onAddButtonClick} />
          <Button
            styles={buttonStylesDanger}
            text="Delete"
            onClick={() => {
              setShowDeleteConfirmationDialog(true);
            }}
            disabled={tableSelections.length === 0 || isLoading}
          />
        </div>
        {!isLoading && ipData.length === 0 ? (
          <MessageBar messageBarType={MessageBarType.warning}>{'No data available'}</MessageBar>
        ) : (
          <DetailsList
            selection={selectionRef.current}
            selectionPreservedOnEmptyClick={true}
            selectionMode={SelectionMode.multiple}
            checkboxVisibility={CheckboxVisibility.always}
            layoutMode={DetailsListLayoutMode.fixedColumns}
            columns={[
              {
                key: 'inboundIP',
                fieldName: 'inboundIP',
                name: 'IPs',
                minWidth: 500,
                onRender: (item: InboundIpItem) => (
                  <>{!isLoading ? item.inboundIP : <SkeletonLoader width="65%" height="50%" />}</>
                ),
              },
            ]}
            styles={firewallManagementTableStyles(theme)}
            items={isLoading ? dummyList : ipData}
            setKey="inboundIP"
          />
        )}
      </div>
      <ConfirmDialog
        hidden={!showDeleteConfirmationDialog}
        title="Confirm Delete"
        msg={
          isDeletionPending
            ? 'Deleting IPs...'
            : tableSelections.length === 1
            ? 'Are you sure you want to delete the selected IP address?'
            : `Are you sure you want to delete ${tableSelections.length} IP addresses?`
        }
        confirmationButtonText={isDeletionPending ? '...' : 'Yes'}
        dismissalButtonText="Cancel"
        onConfirm={handleDelete}
        onDismiss={() => {
          setShowDeleteConfirmationDialog(false);
        }}
        confirmationButtonDisabled={isDeletionPending}
      />
    </>
  );
}
